import { useNavigate } from "@tanstack/react-router";
import { CircleCheck } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { IntentName } from "@/design";
import { useCan } from "@/shared/auth";
import { EmptyState } from "@/shared/components/EmptyState";
import { SectionCard } from "@/shared/components/SectionCard";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";

import { useNotifications } from "../api/use-notifications";
import { actionFor } from "../lib/notif-action";


type NotificationRow = NonNullable<ReturnType<typeof useNotifications>["data"]>[number];

// ponytail: no "severity" domain in status-taxonomy.json, and rule #2 bars
// importing the notifications feature's own map — same 3-line literal.
const SEVERITY_INTENT: Record<"P1" | "P2" | "P3", IntentName> = {
  P1: "danger",
  P2: "warning",
  P3: "neutral",
};

function NotificationRowView({ row }: { row: NotificationRow }): JSX.Element | null {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const action = actionFor(row);
  // `useNotifications` already drops action-less rows; the guard keeps the
  // component honest if rendered with raw data.
  if (action === null) return null;

  return (
    <li
      className="flex items-center gap-4 border-b border-[var(--row-divider)] px-5 py-3 transition-colors last:border-b-0 hover:bg-[var(--row-hover)]"
      data-testid="notification-row"
    >
      <Badge intent={SEVERITY_INTENT[row.severity]} dot data-testid="notification-severity">
        {row.severity}
      </Badge>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-[var(--text-primary)]">
          {row.title}
        </span>
        {/* entity · metric, same as BellDrawer — never `message`, which for
            push failures is the adapter's raw error string. */}
        <span className="truncate text-xs text-[var(--text-subtle)]">
          {[row.entity, row.metric].filter(Boolean).join(" · ")}
        </span>
      </div>
      <time className="shrink-0 text-xs text-[var(--text-subtle)]" data-testid="notification-time">
        {new Date(row.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </time>
      <Button
        variant="outline"
        size="sm"
        onClick={() => void navigate(action.nav)}
        data-testid="notification-action"
        data-action={action.key}
      >
        {t(`dashboard:notif.action.${action.key}`, {
          mkp: action.marketplace ? t(`dashboard:pipeline.mp.${action.marketplace}`) : "",
        })}
      </Button>
    </li>
  );
}

/**
 * "Action required": the newest unacked P1/P2 alerts that have a next step
 * (reconnect a shop, open the order/SKU). Info-only alerts stay in the
 * bell — an Ack button here was just clicks. Gated on `notifications:read`.
 */
export function NotificationsCard(): JSX.Element | null {
  const { t } = useTranslation("dashboard");
  const canRead = useCan("notifications:read");
  const notifications = useNotifications({ enabled: canRead });

  if (!canRead) return null;

  const rows = notifications.data ?? [];

  return (
    <div data-testid="notifications-card">
      <SectionCard title={t("dashboard:notif.title")} caption={t("dashboard:notif.caption")}>
        {rows.length === 0 ? (
          <EmptyState
            icon={<CircleCheck className="h-6 w-6 text-[var(--intent-success-fg)]" />}
            title={t("dashboard:notif.empty")}
          />
        ) : (
          <ul>
            {rows.map((row) => (
              <NotificationRowView key={row.id} row={row} />
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
